/**
 * The generic record surface, the one the server renders as record.html.
 *
 * It is not a stub. It reads the row, shows every field the role's class
 * visibility permits, and attaches the audit drawer and the information button.
 * For a reference record that is the whole of what a bespoke screen would do,
 * and writing twenty bespoke screens that each did exactly this would be twenty
 * places to forget the redaction.
 *
 * The fields arrive already masked where the role's visibility on the section's
 * data class is partial, and already blanked where it is none. This screen
 * cannot unmask anything, because the value never left the server.
 */
import type { Dict, SectionMeta } from '../api/types'
import { useSession } from '../app/session'
import { PageHead } from '../components/PageHead'
import { Panel } from '../components/Panel'
import { KV } from '../components/bits'

export function GenericRecord({ data }: { data: Dict }) {
  const { lbl } = useSession()
  const section = data.section as SectionMeta
  const fields = (data.fields || []) as { label: string; value: string }[]
  const extra = (data.extra_panels || []) as Dict[]

  return (
    <>
      <PageHead section={section} title={data.title as string}
                auditLink={data.audit_link as string | null} />

      <Panel title="This record" explain={section.section_key}>
        <KV rows={fields.map((f) => [lbl(f.label), f.value])} />
      </Panel>

      {extra.map((panel, i) => (
        <Panel key={i} title={(panel.title as string) || 'Related'}
               explain={(panel.section as string) || section.section_key}>
          {Array.isArray(panel.rows) ? (
            <KV rows={(panel.rows as [string, string][])
              .map(([k, v]) => [lbl(k), v])} />
          ) : (
            <p>{String(panel.body ?? '')}</p>
          )}
        </Panel>
      ))}

      <Panel title="Why the fields read as column names" explain={section.section_key}>
        <p className="muted">
          A reference record is shown as the row it is, with the database's own
          names. Renaming the columns on screen would put a second vocabulary
          between the person reading a value and the person who has to find it in
          a query, and the audit trail beside this panel names the same columns.
        </p>
      </Panel>
    </>
  )
}
